
import { useState } from 'react';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import Button from '../../../components/base/Button';

interface ProgressData {
  year: string;
  target: number;
  realisasi: number;
  percentage: number;
  activities: string[];
  challenges: string[];
  achievements: string[];
}

export default function HibahUsahaPage() {
  const [activeTab, setActiveTab] = useState<'overview' | 'progress' | 'impact' | 'methodology'>('overview');

  const progressData: ProgressData[] = [
    {
      year: '2021',
      target: 80,
      realisasi: 67,
      percentage: 84,
      activities: ['Business grant program launch', 'Entrepreneur selection and training', 'Initial funding disbursement'],
      challenges: ['Limited business experience in communities', 'Market access barriers', 'Financial literacy gaps'],
      achievements: ['67 grants awarded successfully', '2.01B IDR disbursed', 'Formation of 12 business cooperatives']
    },
    {
      year: '2022',
      target: 120,
      realisasi: 108,
      percentage: 90,
      activities: ['Expanded grant categories', 'Mentorship program development', 'Market linkage facilitation'],
      challenges: ['Scaling quality with quantity', 'Supply chain development', 'Technology adoption barriers'],
      achievements: ['108 businesses supported', '3.21B IDR funding provided', 'Established 18 market networks']
    },
    {
      year: '2023',
      target: 160,
      realisasi: 147,
      percentage: 92,
      activities: ['Value chain integration', 'Digital marketing training', 'Cooperative strengthening'],
      challenges: ['Market competition increase', 'Quality standardization needs', 'Sustainability planning'],
      achievements: ['147 enterprises launched', '4.37B IDR investment', '25 successful cooperatives']
    },
    {
      year: '2024',
      target: 200,
      realisasi: 189,
      percentage: 95,
      activities: ['Innovation support programs', 'Export market development', 'Sustainability certification'],
      challenges: ['Global market integration', 'Environmental compliance', 'Long-term viability'],
      achievements: ['189 businesses thriving', '5.61B IDR economic impact', '35 certified enterprises']
    }
  ];

  const impactMetrics = [
    { label: 'Business Grants Awarded', value: '511', unit: 'Grants', icon: 'ri-hand-coin-line', color: '#20B2AA' },
    { label: 'Total Funding Disbursed', value: '15.2B', unit: 'IDR', icon: 'ri-money-dollar-circle-line', color: '#32CD32' },
    { label: 'Business Success Rate', value: '78%', unit: 'Survival Rate', icon: 'ri-trophy-line', color: '#FF6B35' },
    { label: 'Jobs Created', value: '1,847', unit: 'Positions', icon: 'ri-team-line', color: '#2D8B57' }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-16 bg-gradient-to-br from-[#20B2AA] to-[#48D1CC] text-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-hand-coin-line text-3xl"></i>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold mb-6">
                Hibah Usaha Program
              </h1>
              <p className="text-xl sm:text-2xl mb-8 opacity-90">
                Economic empowerment through business grants and entrepreneurship development for coastal communities
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  variant="outline" 
                  size="lg"
                  className="bg-white/10 border-white text-white hover:bg-white hover:text-[#20B2AA] whitespace-nowrap"
                >
                  <i className="ri-hand-coin-line mr-2"></i>
                  Apply for Grant
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  className="bg-white/10 border-white text-white hover:bg-white hover:text-[#20B2AA] whitespace-nowrap"
                >
                  <i className="ri-book-line mr-2"></i>
                  Grant Guidelines
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Navigation Tabs */}
        <section className="py-8 bg-white border-b">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-6xl mx-auto">
              <div className="flex flex-wrap gap-2 justify-center">
                {[
                  { id: 'overview', label: 'Program Overview', icon: 'ri-eye-line' },
                  { id: 'progress', label: 'Progress & Targets', icon: 'ri-bar-chart-line' },
                  { id: 'impact', label: 'Impact Assessment', icon: 'ri-award-line' },
                  { id: 'methodology', label: 'Methodology', icon: 'ri-file-text-line' }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as any)}
                    className={`px-6 py-3 rounded-full font-medium transition-all duration-200 whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'bg-[#20B2AA] text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <i className={`${tab.icon} mr-2`}></i>
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <section className="py-16">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-6xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-bold text-[#333333] mb-4">
                    Hibah Usaha Program Overview
                  </h2>
                  <p className="text-lg text-gray-600">
                    Empowering coastal communities through strategic business grants and comprehensive entrepreneurship support
                  </p>
                </div>

                {/* Storytelling Section */}
                <div className="mb-16">
                  <div className="bg-gradient-to-r from-[#20B2AA]/10 to-[#48D1CC]/10 rounded-xl p-8 mb-8">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 bg-[#20B2AA]/10 rounded-full flex items-center justify-center mr-4">
                        <i className="ri-lightbulb-line text-2xl text-[#20B2AA]"></i>
                      </div>
                      <h3 className="text-2xl font-bold text-[#333333]">From Fisherman to Entrepreneur</h3>
                    </div>
                    
                    <div className="prose prose-lg max-w-none">
                      <p className="text-gray-700 leading-relaxed mb-6">
                        "I never imagined I could run my own business," says Ibu Siti, holding up a jar of her homemade mangrove honey. Three years ago, she was struggling to support her family through traditional fishing. Today, she manages a thriving honey cooperative that employs five other women from her village and generates steady income year-round.
                      </p>
                      
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                        <div>
                          <img
                            src="https://public.readdy.ai/ai/img_res/77a9bc2874654c41df35fa7ed50ed6e4.jpg"
                            alt="Women's Cooperative Business"
                            className="w-full h-48 object-cover rounded-lg mb-4"
                          />
                          <p className="text-sm text-gray-600 italic">
                            Ibu Siti showcasing her mangrove honey products at Kuala Kampar cooperative, 2024
                          </p>
                        </div>
                        
                        <div className="flex flex-col justify-center">
                          <blockquote className="border-l-4 border-[#20B2AA] pl-6 mb-6">
                            <p className="text-lg text-gray-700 italic mb-4">
                              "The M4CR business grant didn't just give me money—it gave me confidence and the skills to succeed. Now I employ five other women from my village."
                            </p>
                            <footer className="text-[#20B2AA] font-bold">— Ibu Siti Aminah, Honey Cooperative Leader</footer>
                          </blockquote>
                          
                          <p className="text-gray-700 leading-relaxed">
                            Ibu Siti's transformation represents the core mission of Hibah Usaha: creating sustainable economic opportunities that complement conservation efforts while empowering communities to become self-reliant.
                          </p>
                        </div>
                      </div>
                      
                      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
                        <h5 className="font-bold text-[#333333] mb-3">Beyond Funding: The Ecosystem Approach</h5>
                        <p className="text-gray-700 text-sm leading-relaxed mb-4">
                          M4CR's Hibah Usaha program achieves 78% business success rates by providing comprehensive support beyond funding—including 6 months of mentorship, market linkage development, and peer networks that ensure long-term viability.
                        </p>
                        <div className="grid grid-cols-3 gap-4 text-center">
                          <div>
                            <div className="text-2xl font-bold text-[#20B2AA] mb-1">511</div>
                            <div className="text-xs text-gray-600">Grants awarded</div>
                          </div>
                          <div>
                            <div className="text-2xl font-bold text-[#32CD32] mb-1">15.2B</div>
                            <div className="text-xs text-gray-600">IDR disbursed</div>
                          </div>
                          <div>
                            <div className="text-2xl font-bold text-[#FF6B35] mb-1">78%</div>
                            <div className="text-xs text-gray-600">Success rate</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Journalist Report Section */}
                  <div className="bg-white border border-gray-200 rounded-xl p-8 mb-8">
                    <div className="flex items-center mb-6">
                      <div className="w-12 h-12 bg-[#32CD32]/10 rounded-full flex items-center justify-center mr-4">
                        <i className="ri-newspaper-line text-2xl text-[#32CD32]"></i>
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-[#333333]">Field Report: Microfinance That Works</h3>
                        <p className="text-gray-600">How M4CR achieves 78% business success rates through strategic support</p>
                      </div>
                    </div>
                    
                    <div className="prose prose-lg max-w-none">
                      <div className="bg-[#F8F9FA] border-l-4 border-[#32CD32] p-6 mb-6 rounded-r-lg">
                        <p className="text-sm text-gray-600 mb-2">
                          <strong>Economic Investigation</strong> • December 2024 • M4CR Research Team
                        </p>
                        <h4 className="text-lg font-bold text-[#333333] mb-3">
                          The Secret Behind 78% Success: Why M4CR's Business Grants Outperform Traditional Microfinance
                        </h4>
                      </div>
                      
                      <p className="text-gray-700 leading-relaxed mb-6">
                        <strong>KUALA KAMPAR, Riau</strong> — While traditional microfinance programs often struggle with 40-50% success rates, M4CR's Hibah Usaha program consistently achieves 78% business sustainability after two years. Our investigation reveals the strategic approach that makes the difference between surviving and thriving in Indonesia's coastal economy.
                      </p>
                      
                      <div className="bg-teal-50 border border-teal-200 rounded-lg p-6 mb-6">
                        <h5 className="font-bold text-teal-800 mb-4">The Selection Criteria Revolution</h5>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                          <div>
                            <h6 className="font-semibold text-teal-700 mb-3">Character Assessment</h6>
                            <ul className="space-y-2 text-sm text-teal-700">
                              <li>• Community leadership potential</li>
                              <li>• Environmental stewardship commitment</li>
                              <li>• Collaborative mindset evaluation</li>
                              <li>• Long-term vision alignment</li>
                            </ul>
                          </div>
                          <div>
                            <h6 className="font-semibold text-teal-700 mb-3">Business Viability</h6>
                            <ul className="space-y-2 text-sm text-teal-700">
                              <li>• Market demand validation</li>
                              <li>• Resource availability assessment</li>
                              <li>• Scalability potential analysis</li>
                              <li>• Risk mitigation planning</li>
                            </ul>
                          </div>
                          <div>
                            <h6 className="font-semibold text-teal-700 mb-3">Conservation Integration</h6>
                            <ul className="space-y-2 text-sm text-teal-700">
                              <li>• Environmental impact alignment</li>
                              <li>• Sustainable resource use</li>
                              <li>• Ecosystem service enhancement</li>
                              <li>• Community benefit optimization</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                      
                      <blockquote className="border-l-4 border-[#20B2AA] pl-6 mb-6 bg-[#F8F9FA] p-4 rounded-r-lg">
                        <p className="text-lg text-gray-700 italic mb-4">
                          "We don't just fund good ideas—we fund people with proven commitment to their communities and environment. Our 18-point assessment evaluates character, community ties, and conservation alignment."
                        </p>
                        <footer className="text-[#20B2AA] font-bold">— Pak Dedi Kurniawan, Economic Development Director</footer>
                      </blockquote>
                    </div>
                  </div>
                </div>

                {/* Key Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                  {impactMetrics.map((metric, index) => (
                    <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 text-center">
                      <div 
                        className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                        style={{ backgroundColor: `${metric.color}20` }}
                      >
                        <i className={`${metric.icon} text-2xl`} style={{ color: metric.color }}></i>
                      </div>
                      <div className="text-3xl font-bold mb-2" style={{ color: metric.color }}>
                        {metric.value}
                      </div>
                      <div className="text-sm text-gray-500 mb-1">{metric.unit}</div>
                      <div className="text-[#333333] font-medium">{metric.label}</div>
                    </div>
                  ))}
                </div>

                {/* Program Description */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-12">
                  <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                    <h3 className="text-2xl font-bold text-[#333333] mb-6">Program Objectives</h3>
                    <div className="space-y-4">
                      <div className="flex items-start space-x-4">
                        <div className="w-8 h-8 bg-[#20B2AA]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <i className="ri-check-line text-[#20B2AA]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333] mb-2">Economic Empowerment</h4>
                          <p className="text-gray-600 text-sm">
                            Create sustainable livelihood opportunities through strategic business development
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-4">
                        <div className="w-8 h-8 bg-[#32CD32]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <i className="ri-check-line text-[#32CD32]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333] mb-2">Conservation Integration</h4>
                          <p className="text-gray-600 text-sm">
                            Align business activities with environmental conservation and sustainability goals
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-4">
                        <div className="w-8 h-8 bg-[#FF6B35]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <i className="ri-check-line text-[#FF6B35]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333] mb-2">Community Development</h4>
                          <p className="text-gray-600 text-sm">
                            Build local economic capacity and strengthen community resilience
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start space-x-4">
                        <div className="w-8 h-8 bg-[#2D8B57]/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                          <i className="ri-check-line text-[#2D8B57]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333] mb-2">Women's Empowerment</h4>
                          <p className="text-gray-600 text-sm">
                            Prioritize women-led enterprises and gender-inclusive economic development
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                    <h3 className="text-2xl font-bold text-[#333333] mb-6">Business Categories</h3>
                    <div className="space-y-6">
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <h4 className="font-bold text-[#333333] mb-2">Eco-tourism Services</h4>
                        <p className="text-gray-600 text-sm mb-3">
                          Mangrove tours, homestays, and sustainable tourism experiences
                        </p>
                        <div className="text-xs text-[#20B2AA] font-medium">34% of funded businesses</div>
                      </div>
                      
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <h4 className="font-bold text-[#333333] mb-2">Sustainable Aquaculture</h4>
                        <p className="text-gray-600 text-sm mb-3">
                          Fish farming, shrimp cultivation, and integrated aquaculture systems
                        </p>
                        <div className="text-xs text-[#32CD32] font-medium">28% of funded businesses</div>
                      </div>
                      
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <h4 className="font-bold text-[#333333] mb-2">Mangrove Products</h4>
                        <p className="text-gray-600 text-sm mb-3">
                          Honey production, traditional crafts, and value-added processing
                        </p>
                        <div className="text-xs text-[#FF6B35] font-medium">23% of funded businesses</div>
                      </div>
                      
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <h4 className="font-bold text-[#333333] mb-2">Waste Management</h4>
                        <p className="text-gray-600 text-sm mb-3">
                          Recycling services, composting, and circular economy initiatives
                        </p>
                        <div className="text-xs text-[#2D8B57] font-medium">15% of funded businesses</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Success Stories */}
                <div className="bg-gradient-to-r from-[#20B2AA]/10 to-[#48D1CC]/10 rounded-xl p-8">
                  <h3 className="text-2xl font-bold text-[#333333] mb-8 text-center">Success Stories</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="bg-white rounded-lg p-6">
                      <div className="flex items-center mb-4">
                        <img
                          src="https://readdy.ai/api/search-image?query=Indonesian%20woman%20entrepreneur%20portrait%2C%20confident%20smile%20with%20traditional%20coastal%20products%2C%20successful%20small%20business%20owner%2C%20professional%20headshot%20style%2C%20natural%20lighting%20empowerment%20story&width=60&height=60&seq=testimonial-7&orientation=squarish"
                          alt="Ibu Siti"
                          className="w-12 h-12 rounded-full object-cover mr-4"
                        />
                        <div>
                          <div className="font-bold text-[#333333]">Ibu Siti Aminah</div>
                          <div className="text-sm text-gray-600">Honey Cooperative Leader, Kuala Kampar</div>
                        </div>
                      </div>
                      <p className="text-gray-700 leading-relaxed italic">
                        "The business grant changed my family's life. From selling fish occasionally, now I run a honey cooperative with five employees. We process 200kg of mangrove honey monthly."
                      </p>
                    </div>
                    
                    <div className="bg-white rounded-lg p-6">
                      <div className="flex items-center mb-4">
                        <img
                          src="https://readdy.ai/api/search-image?query=Indonesian%20male%20entrepreneur%20portrait%2C%20confident%20expression%20with%20eco-tourism%20boat%20background%2C%20sustainable%20tourism%20business%20owner%2C%20professional%20headshot%20style%2C%20natural%20lighting%20coastal%20business%20success&width=60&height=60&seq=testimonial-8&orientation=squarish"
                          alt="Pak Hasan"
                          className="w-12 h-12 rounded-full object-cover mr-4"
                        />
                        <div>
                          <div className="font-bold text-[#333333]">Pak Hasan Basri</div>
                          <div className="text-sm text-gray-600">Eco-tourism Guide, Demak</div>
                        </div>
                      </div>
                      <p className="text-gray-700 leading-relaxed italic">
                        "I started with one small boat for mangrove tours. Now I manage a tourism cooperative with 12 boats and 25 guides. We show visitors how conservation creates prosperity."
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Progress Tab */}
        {activeTab === 'progress' && (
          <section className="py-16">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-6xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-bold text-[#333333] mb-4">
                    Progress Tracking & Target Achievement
                  </h2>
                  <p className="text-lg text-gray-600">
                    Four-year journey of scaling business grant programs and economic empowerment
                  </p>
                </div>

                {/* Overall Progress Chart */}
                <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200 mb-12">
                  <h3 className="text-2xl font-bold text-[#333333] mb-8 text-center">Annual Progress Overview</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                    {progressData.map((data, index) => (
                      <div key={index} className="text-center">
                        <div className="text-3xl font-bold text-[#20B2AA] mb-2">{data.year}</div>
                        <div className="text-lg font-medium text-[#333333] mb-2">{data.percentage}%</div>
                        <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                          <div 
                            className="bg-gradient-to-r from-[#20B2AA] to-[#48D1CC] h-3 rounded-full transition-all duration-1000"
                            style={{ width: `${data.percentage}%` }}
                          ></div>
                        </div>
                        <div className="text-sm text-gray-600">
                          {data.realisasi}/{data.target} grants
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="bg-[#F8F9FA] rounded-lg p-6">
                    <h4 className="font-bold text-[#333333] mb-4 text-center">Cumulative Achievement</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-[#20B2AA] mb-1">511</div>
                        <div className="text-sm text-gray-600">Total Grants</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-[#32CD32] mb-1">90.3%</div>
                        <div className="text-sm text-gray-600">Overall Achievement</div>
                      </div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-[#FF6B35] mb-1">15.2B</div>
                        <div className="text-sm text-gray-600">IDR Disbursed</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Detailed Annual Breakdown */}
                <div className="space-y-8">
                  {progressData.map((data, index) => (
                    <div key={index} className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        <div className="lg:col-span-1">
                          <div className="flex items-center mb-4">
                            <div className="w-16 h-16 bg-gradient-to-r from-[#20B2AA] to-[#48D1CC] rounded-full flex items-center justify-center text-white font-bold text-lg mr-4">
                              {data.year}
                            </div>
                            <div>
                              <div className="text-2xl font-bold text-[#333333]">{data.percentage}%</div>
                              <div className="text-gray-600">Achievement Rate</div>
                            </div>
                          </div>
                          
                          <div className="space-y-3">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Target:</span>
                              <span className="font-bold text-[#333333]">{data.target} grants</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">Awarded:</span>
                              <span className="font-bold text-[#20B2AA]">{data.realisasi} grants</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-[#20B2AA] h-2 rounded-full"
                                style={{ width: `${data.percentage}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>

                        <div className="lg:col-span-2">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div>
                              <h4 className="font-bold text-[#333333] mb-3">Key Activities</h4>
                              <ul className="space-y-2">
                                {data.activities.map((activity, idx) => (
                                  <li key={idx} className="flex items-start">
                                    <i className="ri-check-line text-[#20B2AA] mr-2 mt-0.5 flex-shrink-0"></i>
                                    <span className="text-gray-700 text-sm">{activity}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                            
                            <div>
                              <h4 className="font-bold text-[#333333] mb-3">Challenges</h4>
                              <ul className="space-y-2">
                                {data.challenges.map((challenge, idx) => (
                                  <li key={idx} className="flex items-start">
                                    <i className="ri-alert-line text-[#FF6B6B] mr-2 mt-0.5 flex-shrink-0"></i>
                                    <span className="text-gray-700 text-sm">{challenge}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                            
                            <div>
                              <h4 className="font-bold text-[#333333] mb-3">Achievements</h4>
                              <ul className="space-y-2">
                                {data.achievements.map((achievement, idx) => (
                                  <li key={idx} className="flex items-start">
                                    <i className="ri-trophy-line text-[#FFD93D] mr-2 mt-0.5 flex-shrink-0"></i>
                                    <span className="text-gray-700 text-sm">{achievement}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Impact Tab */}
        {activeTab === 'impact' && (
          <section className="py-16">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-6xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-bold text-[#333333] mb-4">
                    Impact Assessment & Outcomes
                  </h2>
                  <p className="text-lg text-gray-600">
                    Measuring economic empowerment and business sustainability outcomes
                  </p>
                </div>

                {/* Impact Metrics Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
                  {impactMetrics.map((metric, index) => (
                    <div key={index} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 text-center hover:shadow-lg transition-shadow duration-300">
                      <div 
                        className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                        style={{ backgroundColor: `${metric.color}20` }}
                      >
                        <i className={`${metric.icon} text-2xl`} style={{ color: metric.color }}></i>
                      </div>
                      <div className="text-3xl font-bold mb-2" style={{ color: metric.color }}>
                        {metric.value}
                      </div>
                      <div className="text-sm text-gray-500 mb-1">{metric.unit}</div>
                      <div className="text-[#333333] font-medium">{metric.label}</div>
                    </div>
                  ))}
                </div>

                {/* Business Performance Assessment */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
                  <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                    <h3 className="text-2xl font-bold text-[#333333] mb-6">Business Performance</h3>
                    <div className="space-y-6">
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-700">Business Survival Rate</span>
                          <span className="font-bold text-[#20B2AA]">78%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-[#20B2AA] h-2 rounded-full" style={{ width: '78%' }}></div>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">Businesses still operating after 2 years</p>
                      </div>
                      
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-700">Revenue Growth</span>
                          <span className="font-bold text-[#32CD32]">156%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-[#32CD32] h-2 rounded-full" style={{ width: '100%' }}></div>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">Average revenue increase within 12 months</p>
                      </div>
                      
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-700">Job Creation Rate</span>
                          <span className="font-bold text-[#FF6B35]">3.6</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-[#FF6B35] h-2 rounded-full" style={{ width: '72%' }}></div>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">Average jobs created per business</p>
                      </div>
                      
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-gray-700">Women Leadership</span>
                          <span className="font-bold text-[#2D8B57]">67%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-[#2D8B57] h-2 rounded-full" style={{ width: '67%' }}></div>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">Businesses led by women entrepreneurs</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                    <h3 className="text-2xl font-bold text-[#333333] mb-6">Economic Impact</h3>
                    <div className="space-y-6">
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <div className="flex items-center mb-3">
                          <div className="w-10 h-10 bg-[#20B2AA]/10 rounded-full flex items-center justify-center mr-3">
                            <i className="ri-money-dollar-circle-line text-[#20B2AA]"></i>
                          </div>
                          <div>
                            <div className="font-bold text-[#333333]">Total Investment</div>
                            <div className="text-2xl font-bold text-[#20B2AA]">15.2B</div>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600">
                          IDR disbursed through business grants program
                        </p>
                      </div>
                      
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <div className="flex items-center mb-3">
                          <div className="w-10 h-10 bg-[#32CD32]/10 rounded-full flex items-center justify-center mr-3">
                            <i className="ri-team-line text-[#32CD32]"></i>
                          </div>
                          <div>
                            <div className="font-bold text-[#333333]">Employment Created</div>
                            <div className="text-2xl font-bold text-[#32CD32]">1,847</div>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600">
                          Direct and indirect jobs created through funded businesses
                        </p>
                      </div>
                      
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <div className="flex items-center mb-3">
                          <div className="w-10 h-10 bg-[#FF6B35]/10 rounded-full flex items-center justify-center mr-3">
                            <i className="ri-line-chart-line text-[#FF6B35]"></i>
                          </div>
                          <div>
                            <div className="font-bold text-[#333333]">ROI Achievement</div>
                            <div className="text-2xl font-bold text-[#FF6B35]">340%</div>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600">
                          Average return on investment for successful businesses
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Long-term Impact */}
                <div className="bg-gradient-to-r from-[#20B2AA]/10 to-[#48D1CC]/10 rounded-xl p-8">
                  <h3 className="text-2xl font-bold text-[#333333] mb-8 text-center">Long-term Impact</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-[#20B2AA]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-community-line text-2xl text-[#20B2AA]"></i>
                      </div>
                      <h4 className="font-bold text-[#333333] mb-2">Community Resilience</h4>
                      <p className="text-gray-600 text-sm">
                        Diversified income sources reduce community vulnerability to economic shocks and environmental changes
                      </p>
                    </div>
                    
                    <div className="text-center">
                      <div className="w-16 h-16 bg-[#32CD32]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-leaf-line text-2xl text-[#32CD32]"></i>
                      </div>
                      <h4 className="font-bold text-[#333333] mb-2">Conservation Economy</h4>
                      <p className="text-gray-600 text-sm">
                        100% of funded businesses align with conservation goals, creating economic incentives for environmental protection
                      </p>
                    </div>
                    
                    <div className="text-center">
                      <div className="w-16 h-16 bg-[#FF6B35]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-women-line text-2xl text-[#FF6B35]"></i>
                      </div>
                      <h4 className="font-bold text-[#333333] mb-2">Women's Empowerment</h4>
                      <p className="text-gray-600 text-sm">
                        67% women-led businesses create leadership opportunities and economic independence for coastal women
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Methodology Tab */}
        {activeTab === 'methodology' && (
          <section className="py-16">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-6xl mx-auto">
                <div className="text-center mb-12">
                  <h2 className="text-3xl font-bold text-[#333333] mb-4">
                    Grant Methodology
                  </h2>
                  <p className="text-lg text-gray-600">
                    Systematic approach to business grant selection, support, and success measurement
                  </p>
                </div>

                {/* Grant Process */}
                <div className="space-y-8 mb-12">
                  {[
                    {
                      step: 1,
                      title: 'Application & Assessment',
                      description: 'Comprehensive evaluation using 18-point criteria for business viability and impact',
                      icon: 'ri-file-text-line',
                      color: '#20B2AA',
                      activities: [
                        'Business plan submission and market analysis',
                        'Character assessment and community validation',
                        'Environmental impact evaluation',
                        'Financial projections and risk assessment'
                      ]
                    },
                    {
                      step: 2,
                      title: 'Selection & Training',
                      description: 'Rigorous selection process followed by comprehensive business training',
                      icon: 'ri-graduation-cap-line',
                      color: '#32CD32',
                      activities: [
                        'Panel interview and community endorsement',
                        'Business development training program',
                        'Financial literacy and management skills',
                        'Market linkage and networking facilitation'
                      ]
                    },
                    {
                      step: 3,
                      title: 'Funding & Mentorship',
                      description: 'Phased funding disbursement with ongoing mentorship and support',
                      icon: 'ri-hand-coin-line',
                      color: '#FF6B35',
                      activities: [
                        'Milestone-based funding disbursement',
                        '6-month intensive mentorship program',
                        'Peer network development and support',
                        'Technical assistance and problem-solving'
                      ]
                    },
                    {
                      step: 4,
                      title: 'Monitoring & Scaling',
                      description: 'Continuous monitoring with opportunities for business expansion',
                      icon: 'ri-line-chart-line',
                      color: '#2D8B57',
                      activities: [
                        'Monthly performance tracking and reporting',
                        'Market expansion and scaling support',
                        'Sustainability planning and certification',
                        'Success story documentation and sharing'
                      ]
                    }
                  ].map((step, index) => (
                    <div key={index} className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                      <div className="flex items-start space-x-6">
                        <div 
                          className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-xl flex-shrink-0"
                          style={{ backgroundColor: step.color }}
                        >
                          {step.step}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center mb-4">
                            <i className={`${step.icon} text-2xl mr-3`} style={{ color: step.color }}></i>
                            <h3 className="text-2xl font-bold text-[#333333]">{step.title}</h3>
                          </div>
                          <p className="text-gray-700 leading-relaxed text-lg mb-6">
                            {step.description}
                          </p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {step.activities.map((activity, idx) => (
                              <div key={idx} className="flex items-start space-x-3">
                                <div 
                                  className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                                  style={{ backgroundColor: `${step.color}20` }}
                                >
                                  <i className="ri-check-line text-xs" style={{ color: step.color }}></i>
                                </div>
                                <span className="text-gray-700 text-sm">{activity}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Support Framework */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
                  <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                    <h3 className="text-2xl font-bold text-[#333333] mb-6">Financial Support</h3>
                    <div className="space-y-4">
                      <div className="flex items-center space-x-4 p-4 bg-[#F8F9FA] rounded-lg">
                        <div className="w-10 h-10 bg-[#20B2AA]/10 rounded-full flex items-center justify-center">
                          <i className="ri-money-dollar-circle-line text-[#20B2AA]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333]">Micro-grants</h4>
                          <p className="text-sm text-gray-600">IDR 5-50 million per business</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4 p-4 bg-[#F8F9FA] rounded-lg">
                        <div className="w-10 h-10 bg-[#32CD32]/10 rounded-full flex items-center justify-center">
                          <i className="ri-calendar-check-line text-[#32CD32]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333]">Phased Disbursement</h4>
                          <p className="text-sm text-gray-600">Milestone-based funding release</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4 p-4 bg-[#F8F9FA] rounded-lg">
                        <div className="w-10 h-10 bg-[#FF6B35]/10 rounded-full flex items-center justify-center">
                          <i className="ri-shield-check-line text-[#FF6B35]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333]">No Collateral</h4>
                          <p className="text-sm text-gray-600">Interest-free grants with no collateral</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-4 p-4 bg-[#F8F9FA] rounded-lg">
                        <div className="w-10 h-10 bg-[#2D8B57]/10 rounded-full flex items-center justify-center">
                          <i className="ri-trophy-line text-[#2D8B57]"></i>
                        </div>
                        <div>
                          <h4 className="font-bold text-[#333333]">Performance Bonus</h4>
                          <p className="text-sm text-gray-600">Additional funding for high performers</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
                    <h3 className="text-2xl font-bold text-[#333333] mb-6">Technical Assistance</h3>
                    <div className="space-y-6">
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <div className="flex items-center mb-3">
                          <div className="w-10 h-10 bg-[#20B2AA]/10 rounded-full flex items-center justify-center mr-3">
                            <i className="ri-user-heart-line text-[#20B2AA]"></i>
                          </div>
                          <h4 className="font-bold text-[#333333]">Business Mentorship</h4>
                        </div>
                        <p className="text-sm text-gray-600">
                          6-month intensive mentorship with experienced entrepreneurs
                        </p>
                      </div>
                      
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <div className="flex items-center mb-3">
                          <div className="w-10 h-10 bg-[#32CD32]/10 rounded-full flex items-center justify-center mr-3">
                            <i className="ri-book-line text-[#32CD32]"></i>
                          </div>
                          <h4 className="font-bold text-[#333333]">Financial Literacy</h4>
                        </div>
                        <p className="text-sm text-gray-600">
                          Comprehensive training in financial management and planning
                        </p>
                      </div>
                      
                      <div className="bg-[#F8F9FA] rounded-lg p-4">
                        <div className="flex items-center mb-3">
                          <div className="w-10 h-10 bg-[#FF6B35]/10 rounded-full flex items-center justify-center mr-3">
                            <i className="ri-megaphone-line text-[#FF6B35]"></i>
                          </div>
                          <h4 className="font-bold text-[#333333]">Marketing Support</h4>
                        </div>
                        <p className="text-sm text-gray-600">
                          Branding, marketing strategy, and digital presence development
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Success Metrics */}
                <div className="bg-[#F8F9FA] rounded-xl p-8">
                  <h3 className="text-2xl font-bold text-[#333333] mb-8 text-center">Success Metrics</h3>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                    <div className="text-center">
                      <div className="w-16 h-16 bg-[#20B2AA]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-line-chart-line text-2xl text-[#20B2AA]"></i>
                      </div>
                      <h4 className="font-bold text-[#333333] mb-2">Business Viability</h4>
                      <p className="text-gray-600 text-sm mb-2">2-year survival</p>
                      <div className="text-xs text-gray-500">Target: &gt;70%</div>
                    </div>
                    
                    <div className="text-center">
                      <div className="w-16 h-16 bg-[#32CD32]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-team-line text-2xl text-[#32CD32]"></i>
                      </div>
                      <h4 className="font-bold text-[#333333] mb-2">Job Creation</h4>
                      <p className="text-gray-600 text-sm mb-2">Employment generated</p>
                      <div className="text-xs text-gray-500">Avg: 3.6 jobs/grant</div>
                    </div>
                    
                    <div className="text-center">
                      <div className="w-16 h-16 bg-[#FF6B35]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-leaf-line text-2xl text-[#FF6B35]"></i>
                      </div>
                      <h4 className="font-bold text-[#333333] mb-2">Environmental Impact</h4>
                      <p className="text-gray-600 text-sm mb-2">Conservation alignment</p>
                      <div className="text-xs text-gray-500">100% requirement</div>
                    </div>
                    
                    <div className="text-center">
                      <div className="w-16 h-16 bg-[#2D8B57]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-money-dollar-circle-line text-2xl text-[#2D8B57]"></i>
                      </div>
                      <h4 className="font-bold text-[#333333] mb-2">ROI</h4>
                      <p className="text-gray-600 text-sm mb-2">Return on investment</p>
                      <div className="text-xs text-gray-500">Avg: 340%</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Call to Action */}
        <section className="py-16 bg-[#20B2AA] text-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-6">
                Start Your Business Journey
              </h2>
              <p className="text-xl mb-8 opacity-90">
                Transform your entrepreneurial vision into reality with comprehensive business grants and support from M4CR.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  variant="outline" 
                  size="lg"
                  className="bg-white/10 border-white text-white hover:bg-white hover:text-[#20B2AA] whitespace-nowrap"
                  href="/program"
                >
                  <i className="ri-arrow-left-line mr-2"></i>
                  Back to Program
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  className="bg-white/10 border-white text-white hover:bg-white hover:text-[#20B2AA] whitespace-nowrap"
                  href="/tentang"
                >
                  <i className="ri-information-line mr-2"></i>
                  Learn More About M4CR
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
